import React, { useState, useEffect, useCallback } from 'react';
import { auth, db, storage, functions } from '../../pages/app/protected/firebase';
import { doc, getDoc, setDoc, collection, addDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';

export default function MyAccount() {
  const { user, role } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    countryCode: '+60',
    profilePictureUrl: ''
  });

  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [confirmDeleteChecked, setConfirmDeleteChecked] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);

  const countryCodes = ['+60', '+65', '+62', '+66'];

  // Wrap fetchData in useCallback to avoid infinite re-renders
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoadingPage(true);
    try {
      // First get the user's company ID
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const companyId = userDoc.data().companyId;
      if (!companyId) {
        throw new Error('No company associated with this user');
      }

      // Now get the profile from the company's members collection
      const profileRef = doc(db, 'companies', companyId, 'members', user.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        setFormData(profileSnap.data());
        if (profileSnap.data().profilePictureUrl) {
          setPreviewUrl(profileSnap.data().profilePictureUrl);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoadingPage(false);
    }
  }, [user]);

  // Load data when component mounts or user changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/^0+/, '');
    setFormData(prev => ({ ...prev, phone: cleanedValue }));
  };

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Get the user's company ID
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const companyId = userDoc.data().companyId;
      if (!companyId) {
        throw new Error('No company associated with this user');
      }

      let profilePictureUrl = formData.profilePictureUrl;

      if (profilePictureFile) {
        const storageRef = ref(storage, `companies/${companyId}/members/${user.uid}/profilePicture/${profilePictureFile.name}`);
        await uploadBytes(storageRef, profilePictureFile);
        profilePictureUrl = await getDownloadURL(storageRef);

        // Update file manager in the company's context
        await addDoc(collection(db, 'companies', companyId, 'fileManager'), {
          filename: profilePictureFile.name,
          url: profilePictureUrl,
          type: profilePictureFile.type,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user.uid
        });
      }

      // Save profile in the company's members collection
      const profileRef = doc(db, 'companies', companyId, 'members', user.uid);
      await setDoc(profileRef, {
        ...formData,
        profilePictureUrl,
        email: user.email,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Also update the user's basic profile for quick access
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: formData.name || user.displayName,
        photoURL: profilePictureUrl || user.photoURL,
        updatedAt: new Date().toISOString()
      });

      setMessage('Profile updated successfully');
      setProfilePictureFile(null);
      await fetchData(); // Refresh the data
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setModalLoading(true);
    setModalError('');
    setModalMessage('');

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await sendPasswordResetEmail(auth, user.email);
      setModalMessage('Password reset link sent to your email.');
      setCurrentPassword('');
    } catch (err) {
      console.error(err);
      setModalError('Incorrect password. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setModalLoading(true);
    setModalError('');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      const deleteAccountFn = httpsCallable(functions, 'deleteUserAndData');
      await deleteAccountFn({ uid: user.uid });
    } catch (err) {
      console.error(err);
      setModalError('Failed to delete account. Please ensure the password is correct.');
    } finally {
      setModalLoading(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading account information...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-8 rounded-2xl shadow-md">
      <h2 className="text-3xl font-bold mb-8 text-white">My Account</h2>

      <div className="flex items-center gap-6 mb-8">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-[#1f1f1f]">
          {previewUrl ? (
            <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">No Image</div>
          )}
        </div>
        <div>
          <label className="block text-sm mb-2 text-gray-400">Change Profile Picture</label>
          <input type="file" accept="image/*" onChange={handleProfilePictureChange} className="text-white" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block mb-2 text-gray-400">First Name <span className="text-red-500">*</span></label>
          <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 bg-[#1f1f1f] text-white rounded-lg" />
        </div>
        <div>
          <label className="block mb-2 text-gray-400">Last Name <span className="text-red-500">*</span></label>
          <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 bg-[#1f1f1f] text-white rounded-lg" />
        </div>
      </div>

      <div className="mb-8">
        <label className="block mb-2 text-gray-400">Role</label>
        <input type="text" value={role || ''} disabled className="w-full px-4 py-3 rounded-lg bg-[#1f1f1f] text-gray-400 cursor-not-allowed" />
      </div>

      <div className="mb-8">
      <label className="block mb-2 text-gray-400">Phone Number <span className="text-red-500">*</span></label>
          <div className="flex text-white gap-2">
            <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="bg-[#1f1f1f] rounded-lg px-4 py-3">
              {countryCodes.map(code => <option key={code} value={code}>{code}</option>)}
            </select>
            <input name="phone" value={formData.phone} onChange={handlePhoneChange} className="flex-1 bg-[#1f1f1f] rounded-lg px-4 py-3" />
          </div>
      </div>

      <div className="mb-8">
        <label className="block mb-2 text-gray-400">Email</label>
        <input type="email" value={user?.email || ''} disabled className="w-full px-4 py-3 rounded-lg bg-[#1f1f1f] text-gray-400 cursor-not-allowed" />
      </div>

      <div className="flex gap-4">
        <button onClick={handleSave} disabled={loading || !formData.firstName || !formData.lastName || !formData.phone} className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-bold transition disabled:opacity-50">
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={() => setResetModalOpen(true)} className="text-sm text-purple-400 hover:underline">Change Password</button>
        <button onClick={() => setDeleteModalOpen(true)} className="text-sm text-red-400 hover:underline">Delete Account</button>
      </div>

      {message && <p className="mt-6 text-green-400 font-semibold">{message}</p>}
      {error && <p className="mt-6 text-red-400 font-semibold">{error}</p>}

      {/* 🔒 Password Reset Modal */}
{resetModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
    <div className="bg-[#1f1f1f] p-8 rounded-2xl w-full max-w-md">
      <h3 className="text-2xl font-bold text-white mb-6">Confirm Current Password</h3>

      <input
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        placeholder="Enter current password"
        className="w-full px-4 py-3 rounded-lg bg-[#2a2a2a] text-white mb-4"
      />

      {modalMessage && <p className="text-green-400 text-sm mb-4">{modalMessage}</p>}
      {modalError && <p className="text-red-400 text-sm mb-4">{modalError}</p>}

      <div className="flex justify-end gap-4">
        <button
          onClick={() => {
            setResetModalOpen(false);
            setCurrentPassword('');
            setModalError('');
            setModalMessage('');
          }}
          className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
        >
          Cancel
        </button>
        <button
          onClick={handlePasswordReset}
          disabled={modalLoading}
          className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
        >
          {modalLoading ? 'Processing...' : 'Send Reset Link'}
        </button>
      </div>
    </div>
  </div>
)}

{/* ❌ Delete Account Modal */}
{deleteModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
    <div className="bg-[#1f1f1f] p-8 rounded-2xl w-full max-w-md">
      <h3 className="text-2xl font-bold text-white mb-6">Delete Account</h3>

      <p className="text-red-400 text-sm mb-4">
        ⚠️ Please <strong>cancel your active subscription</strong> before deleting your account to avoid future charges.
        This action is <strong>permanent</strong> and <u>cannot be undone</u>.
      </p>

      <input
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        placeholder="Enter current password"
        className="w-full px-4 py-3 rounded-lg bg-[#2a2a2a] text-white mb-4"
      />

      <label className="flex items-start text-sm text-white mb-4 space-x-2">
        <input
          type="checkbox"
          checked={confirmDeleteChecked}
          onChange={(e) => setConfirmDeleteChecked(e.target.checked)}
          className="mt-1"
        />
        <span>I understand this will <span className="text-red-400">permanently delete my account</span> and cannot be undone.</span>
      </label>

      {modalError && <p className="text-red-400 text-sm mb-4">{modalError}</p>}

      <div className="flex justify-end gap-4">
        <button
          onClick={() => {
            setDeleteModalOpen(false);
            setCurrentPassword('');
            setConfirmDeleteChecked(false);
            setModalError('');
          }}
          className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
        >
          Cancel
        </button>
        <button
          onClick={handleDeleteAccount}
          disabled={modalLoading || !confirmDeleteChecked}
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
        >
          {modalLoading ? 'Processing...' : 'Delete Account'}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}
